import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { loadSlexKitRuntime, loadSlexKitTooling } from "./runtime-loader.js";
import HighlightedMarkdownCode from "./HighlightedMarkdownCode.svelte";

let nextPreviewId = 0;

function createPreviewDomainId() {
	nextPreviewId += 1;

	return `slexkit_preview_${nextPreviewId.toString(36)}`;
}

var root = $.from_html(`<div><div class="slex-streamdown-body"><div class="slex-streamdown-loading">Loading SlexKit runtime...</div></div></div>`);
var root_1 = $.from_html(`<div><div class="slex-streamdown-body"><div class="slex-streamdown-loading">Rendering SlexKit...</div></div></div>`);
var root_2 = $.from_html(`<div class="slex-streamdown-error" role="alert"><div class="slex-streamdown-error-title">Failed to render SlexKit</div> <div class="slex-streamdown-error-message"> </div></div>`);
var root_3 = $.from_html(`<div></div>`);
var root_4 = $.from_html(`<div class="slex-streamdown-error-detail"> </div>`);
var root_5 = $.from_html(`<div class="slex-streamdown-error-location"> </div> <!> <pre class="slex-streamdown-error-excerpt"> </pre>`, 1);
var root_6 = $.from_html(`<div class="slex-streamdown-error" role="alert"><div class="slex-streamdown-error-title"> </div> <div class="slex-streamdown-error-message"> </div> <!></div>`);
var root_7 = $.from_html(`<div><div class="slex-streamdown-body"><!></div></div>`);

export default function SlexCode($$anchor, $$props) {
	$.push($$props, true);

	let lang = $.prop($$props, 'lang', 3, ""),
		text = $.prop($$props, 'text', 3, ""),
		renderMode = $.prop($$props, 'renderMode', 3, "component"),
		runtime = $.prop($$props, 'runtime', 3, "trusted"),
		useGlobalRuntimeHost = $.prop($$props, 'useGlobalRuntimeHost', 3, false),
		securePolicy = $.prop($$props, 'securePolicy', 19, () => ({})),
		secureFrame = $.prop($$props, 'secureFrame', 3, true),
		playgroundUrl = $.prop($$props, 'playgroundUrl', 3, "/playground.html"),
		className = $.prop($$props, 'className', 3, ""),
		streaming = $.prop($$props, 'streaming', 3, false),
		incomplete = $.prop($$props, 'incomplete', 3, false);

	let runtimeApi = $.state(null);
	let runtimeError = $.state(null);
	let runtimeLoadError = $.state(null);
	let runtimeLoadToken = 0;
	const previewId = createPreviewDomainId();
	const info = $.derived(() => parseCodeInfo(lang()));
	const isSlexKit = $.derived(() => $.get(info).language === "slex");

	const activeRuntimeHost = $.derived(() => $$props.runtimeHost ?? (useGlobalRuntimeHost() && $.get(runtimeApi)
		? $.get(runtimeApi).getSlexKitMarkdownRuntimeHost()
		: undefined));

	const activeRuntimeMode = $.derived(() => $.get(activeRuntimeHost)?.getMode());
	const isSecureRuntime = $.derived(() => runtime() === "secure");
	const usesRuntimeHost = $.derived(() => !!$.get(activeRuntimeHost));
	const delegatesToSecureHost = $.derived(() => $.get(activeRuntimeMode) === "secure");
	const streamingMode = $.derived(() => normalizeStreamingMode(streaming()));

	const streamingResult = $.derived(() => $.get(isSlexKit) && $.get(runtimeApi) && !$.get(isSecureRuntime) && !$.get(delegatesToSecureHost) && $.get(streamingMode) !== false
		? incomplete()
			? $.get(runtimeApi).parseSlexStreamingSource(text(), { mode: $.get(streamingMode) })
			: parseFinalSlexSource($.get(runtimeApi), text())
		: null);

	const sourcePending = $.derived(() => Boolean($.get(isSlexKit) && $.get(runtimeApi) && (incomplete() && $.get(streamingMode) === false || incomplete() && $.get(streamingMode) !== false && ($.get(isSecureRuntime) || $.get(delegatesToSecureHost)) || incomplete() && $.get(streamingMode) !== false && $.get(streamingResult)?.status === "pending")));
	const parsedSource = $.derived(() => $.get(isSlexKit) && $.get(runtimeApi) && !$.get(sourcePending) && !$.get(isSecureRuntime) && !$.get(delegatesToSecureHost) && $.get(streamingMode) === false ? $.get(runtimeApi).parseSlexSource(text()) : null);
	const isPreviewSource = $.derived(() => $.get(streamingResult)?.status === "repaired");

	const parsedValue = $.derived(() => $.get(streamingResult)?.status === "complete" || $.get(streamingResult)?.status === "repaired"
		? $.get(streamingResult).value
		: $.get(parsedSource)?.ok ? $.get(parsedSource).value : undefined);

	const previewDomain = $.derived(() => `${$$props.domain ? `${$$props.domain}::` : ""}${previewId}`);
	const runtimeDomain = $.derived(() => $.get(isPreviewSource) ? $.get(previewDomain) : $$props.domain);

	const previewNamespace = $.derived(() => $.get(isPreviewSource)
		? scopedNamespace($.get(parsedValue), $.get(runtimeDomain))
		: undefined);

	const sourceKind = $.derived(() => $.get(parsedValue) && isStateOnlySource($.get(parsedValue)) ? "state-only" : "renderable");

	const runtimeInput = $.derived(() => $.get(isSecureRuntime) || $.get(usesRuntimeHost)
		? $.get(isPreviewSource) && $.get(streamingResult)?.status === "repaired" ? $.get(streamingResult).repairedSource : text()
		: scopedSlexKitInput(text(), $.get(parsedValue), $.get(runtimeDomain)));

	const effectiveRenderMode = $.derived(() => resolveRenderMode($.get(info).meta, renderMode()));

	const parseError = $.derived(() => $.get(streamingResult)?.status === "invalid"
		? $.get(streamingResult).error
		: $.get(parsedSource) && !$.get(parsedSource).ok ? $.get(parsedSource).error : null);

	const displayError = $.derived(() => $.get(sourcePending)
		? null
		: $.get(parseError) ?? $.get(runtimeError) ?? $.get(runtimeLoadError));

	const previewMinHeight = $.derived(() => playgroundOption($.get(info).meta, "previewMinHeight", playgroundOption($.get(info).meta, "height", "360px")));
	const title = $.derived(() => playgroundOption($.get(info).meta, "title", "SlexKit playground"));
	const playgroundMode = $.derived(() => playgroundOption($.get(info).meta, "mode", playgroundOption($.get(info).meta, "webMode", "render")));

	const blockClass = $.derived(() => [
		className(),
		`slex-doc-slexkit-demo--${$.get(effectiveRenderMode)}`
	].filter(Boolean).join(" "));

	const playgroundClass = $.derived(() => [className(), "slex-doc-slexkit-demo--playground"].filter(Boolean).join(" "));

	function runtimeLabels() {
		const locale = document.documentElement.lang;

		if (locale === "en-US") {
			return {
				"button.label": "Button",
				"collapsible.trigger": "Expand",
				"select.placeholder": "Select",
				"submit.ignore": "Ignore",
				"submit.submit": "Submit",
				"toast.close": "Close notification"
			};
		}

		return {
			"button.label": "按钮",
			"collapsible.trigger": "展开",
			"select.placeholder": "请选择",
			"submit.ignore": "忽略",
			"submit.submit": "提交",
			"toast.close": "关闭通知"
		};
	}

	function runtimeMountOptions() {
		return {
			dir: document.documentElement.dir || "auto",
			labels: runtimeLabels()
		};
	}

	function configureRuntimeHost() {
		$.get(activeRuntimeHost)?.configure({
			mode: runtime(),
			policy: securePolicy(),
			hostAdapter: $$props.hostAdapter,
			secureFrame: secureFrame()
		});
	}

	$.user_effect(() => {
		if (!$.get(isSlexKit)) return;

		const token = ++runtimeLoadToken;

		$.set(runtimeLoadError, null);

		loadSlexKitRuntime().then((api) => {
			if (token === runtimeLoadToken) $.set(runtimeApi, api, true);
		}).catch((error) => {
			if (token === runtimeLoadToken) $.set(runtimeLoadError, error, true);
		});
	});

	$.user_effect(() => {
		$.set(runtimeError, null);

		if (!$.get(runtimeApi) || !$.get(isSlexKit) || $.get(sourcePending) || $.get(isSecureRuntime) || $.get(delegatesToSecureHost) || $.get(sourceKind) !== "state-only") return;

		if ($.get(activeRuntimeHost)) {
			const container = document.createElement("span");

			try {
				configureRuntimeHost();

				const cleanup = $.get(activeRuntimeHost).mountBlock({
					artifactId: $.get(runtimeDomain),
					executionMode: $.get(isPreviewSource) ? "preview" : "live",
					source: $.get(runtimeInput),
					container,
					...runtimeMountOptions()
				});

				return () => {
					cleanup();

					if ($.get(isPreviewSource) && $.get(runtimeDomain)) $.get(activeRuntimeHost).disposeArtifact($.get(runtimeDomain));
				};
			} catch(error) {
				$.set(runtimeError, error, true);

				return;
			}
		}

		if (!$.get(runtimeApi).ingest($.get(runtimeInput))) {
			$.set(runtimeError, new Error("Failed to parse Slex state block."), true);
		}

		if ($.get(isPreviewSource) && $.get(previewNamespace)) return () => $.get(runtimeApi).disposeNamespace($.get(previewNamespace));
	});

	function renderPreview(node) {
		let cleanup;
		let mountedPreviewArtifactId;
		let mountedPreviewNamespace;

		const disposePreviewMount = () => {
			if (mountedPreviewArtifactId) $.get(activeRuntimeHost)?.disposeArtifact(mountedPreviewArtifactId);
			if (mountedPreviewNamespace) $.get(runtimeApi)?.disposeNamespace(mountedPreviewNamespace);

			mountedPreviewArtifactId = undefined;
			mountedPreviewNamespace = undefined;
		};

		const render = () => {
			cleanup?.();
			disposePreviewMount();
			node.replaceChildren();
			$.set(runtimeError, null);

			if ($.get(sourcePending) || $.get(displayError) || !$.get(runtimeApi) || !$.get(isSlexKit) || $.get(sourceKind) === "state-only") return;

			try {
				if ($.get(activeRuntimeHost)) configureRuntimeHost();

				cleanup = $.get(activeRuntimeHost)
					? $.get(activeRuntimeHost).mountBlock({
						artifactId: $.get(runtimeDomain),
						executionMode: $.get(isPreviewSource) ? "preview" : "live",
						source: $.get(runtimeInput),
						container: node,
						...runtimeMountOptions()
					})
					: $.get(isSecureRuntime)
						? $.get(runtimeApi).mountSecureArtifact($.get(runtimeInput), node, {
							...runtimeMountOptions(),
							policy: securePolicy(),
							hostAdapter: $$props.hostAdapter,
							frame: secureFrame()
						})
						: $.get(runtimeApi).mount($.get(runtimeInput), node, {
							...runtimeMountOptions(),
							executionMode: $.get(isPreviewSource) ? "preview" : "live"
						});

				if ($.get(isPreviewSource)) {
					mountedPreviewArtifactId = $.get(activeRuntimeHost) ? $.get(runtimeDomain) : undefined;
					mountedPreviewNamespace = $.get(activeRuntimeHost) ? undefined : $.get(previewNamespace);
				}

				if (!node.querySelector(".slexkit-root")) {
					if ($.get(delegatesToSecureHost)) return;
					if ($.get(isSecureRuntime) && node.querySelector("iframe[data-slexkit-secure-frame='true']")) return;

					throw new Error("SlexKit did not render a root. Check the Slex source syntax.");
				}
			} catch(error) {
				try {
					cleanup?.();
				} finally {
					node.replaceChildren();
				}

				$.set(runtimeError, error, true);
			}
		};

		render();

		return {
			update() {
				render();
			},

			destroy() {
				cleanup?.();
				disposePreviewMount();
				node.replaceChildren();
			}
		};
	}

	function renderPlayground(node) {
		let cleanup;
		let active = true;

		const render = async () => {
			cleanup?.();
			node.replaceChildren();

			if (!$.get(runtimeApi)) return;

			try {
				await loadSlexKitTooling();
			} catch(error) {
				$.set(runtimeError, error, true);

				return;
			}

			if (!active) return;

			cleanup = $.get(runtimeApi).mount(
				{
					slex: "0.1",
					namespace: $$props.domain || "site_markdown_playground",
					g: {},
					layout: {
						"playground:inline": {
							class: $.get(playgroundClass),
							domain: $$props.domain || "site_markdown_playground",
							mode: $.get(playgroundMode),
							playgroundUrl: playgroundUrl(),
							previewMinHeight: $.get(previewMinHeight),
							source: text(),
							sourceType: "slex",
							title: $.get(title)
						}
					}
				},
				node,
				runtimeMountOptions()
			);
		};

		render();

		return {
			update() {
				void render();
			},

			destroy() {
				active = false;
				cleanup?.();
				node.replaceChildren();
			}
		};
	}

	function parseCodeInfo(value = "") {
		const raw = String(value ?? "").trim();
		const [language = "", ...metaParts] = raw.split(/\s+/);

		return { language: language.toLowerCase(), meta: metaParts.join(" ") };
	}

	function parseFenceOptions(meta = "") {
		const options = {};

		for (const match of String(meta).matchAll(/([A-Za-z0-9_-]+)=("([^"]*)"|'([^']*)'|([^\s]+))/g)) {
			options[match[1]] = match[3] ?? match[4] ?? match[5] ?? "";
		}

		return options;
	}

	function resolveRenderMode(meta, fallback) {
		const options = parseFenceOptions(meta);
		const value = String(options.render ?? options.as ?? options.mode ?? "").toLowerCase();

		if (value === "playground" || value === "editor" || value === "workbench") return "playground";
		if (value === "component" || value === "render" || value === "preview") return "component";

		return fallback;
	}

	function playgroundOption(meta, key, fallback = "") {
		return parseFenceOptions(meta)[key] || fallback;
	}

	function normalizeStreamingMode(mode) {
		if (mode === true) return "repair";
		if (mode === undefined) return false;

		return mode;
	}

	function parseFinalSlexSource(api, source) {
		const parsed = api.parseSlexSource(source);

		return parsed.ok
			? { status: "complete", source, value: parsed.value }
			: {
				status: "invalid",
				source,
				error: parsed.error,
				diagnostic: parsed.diagnostic
			};
	}

	function errorMessage(error) {
		return error instanceof Error ? error.message : String(error);
	}

	function errorDiagnostic(error) {
		if (!isRecord(error)) return null;

		const diagnostic = error.diagnostic;

		if (!isRecord(diagnostic)) return null;

		if (typeof diagnostic.message !== "string" || typeof diagnostic.line !== "number" || typeof diagnostic.column !== "number" || typeof diagnostic.excerpt !== "string") {
			return null;
		}

		return {
			message: diagnostic.message,
			line: diagnostic.line,
			column: diagnostic.column,
			detail: typeof diagnostic.detail === "string" ? diagnostic.detail : undefined,
			excerpt: diagnostic.excerpt
		};
	}

	function isRecord(value) {
		return !!value && typeof value === "object" && !Array.isArray(value);
	}

	function isRenderableSource(value) {
		if (!isRecord(value)) return false;
		if ("layout" in value) return isRecord(value.layout) && Object.keys(value.layout).length > 0;
		if ("namespace" in value || "g" in value) return false;

		return Object.keys(value).some((key) => key.includes(":"));
	}

	function isStateOnlySource(value) {
		return isRecord(value) && !isRenderableSource(value) && ("slex" in value || "namespace" in value || "g" in value);
	}

	function scopedSlexKitInput(code, source, scope) {
		if (!scope || !isRecord(source)) return code;

		if (!("slex" in source || "namespace" in source || "g" in source || "layout" in source)) {
			if (!isRenderableSource(source)) return code;

			return { namespace: `${scope}::default`, g: {}, layout: source };
		}

		const namespace = String(source.namespace || "default");

		if (!("layout" in source) && isRenderableSource(source)) {
			const {
				slex,
				namespace: _namespace,
				g,
				layout: _layout,
				...bareLayout
			} = source;

			return {
				...typeof slex === "string" ? { slex } : {},
				namespace: `${scope}::${namespace}`,
				...isRecord(g) ? { g } : {},
				layout: bareLayout
			};
		}

		return { ...source, namespace: `${scope}::${namespace}` };
	}

	function scopedNamespace(source, scope) {
		if (!scope || !isRecord(source)) return undefined;

		if (!("slex" in source || "namespace" in source || "g" in source || "layout" in source)) {
			return isRenderableSource(source) ? `${scope}::default` : undefined;
		}

		return `${scope}::${String(source.namespace || "default")}`;
	}

	var fragment = $.comment();
	var node_1 = $.first_child(fragment);

	{
		var consequent = ($$anchor) => {
			HighlightedMarkdownCode($$anchor, {
				get lang() {
					return lang();
				},

				get text() {
					return text();
				}
			});
		};

		var consequent_1 = ($$anchor) => {
			var div = root();

			$.template_effect(($0) => $.set_class(div, 1, $0), [
				() => $.clsx(`slex-streamdown-block ${$.get(blockClass)}`.trim())
			]);

			$.append($$anchor, div);
		};

		var consequent_2 = ($$anchor) => {
			var div_1 = root_1();

			$.template_effect(($0) => $.set_class(div_1, 1, $0), [
				() => $.clsx(`slex-streamdown-block ${$.get(blockClass)}`.trim())
			]);

			$.append($$anchor, div_1);
		};

		var consequent_4 = ($$anchor) => {
			var fragment_2 = $.comment();
			var node_2 = $.first_child(fragment_2);

			{
				var consequent_3 = ($$anchor) => {
					var div_2 = root_2();
					var div_3 = $.sibling($.child(div_2), 2);
					var text_1 = $.child(div_3, true);

					$.reset(div_3);
					$.reset(div_2);
					$.template_effect(($0) => $.set_text(text_1, $0), [() => errorMessage($.get(displayError))]);
					$.append($$anchor, div_2);
				};

				$.if(node_2, ($$render) => {
					if ($.get(displayError)) $$render(consequent_3);
				});
			}

			$.append($$anchor, fragment_2);
		};

		var consequent_5 = ($$anchor) => {
			var div_4 = root_3();

			$.action(div_4, ($$node, $$action_arg) => renderPlayground?.($$node, $$action_arg), text);
			$.append($$anchor, div_4);
		};

		var alternate_1 = ($$anchor) => {
			var div_5 = root_7();
			var div_6 = $.child(div_5);
			var node_3 = $.child(div_6);

			{
				var consequent_8 = ($$anchor) => {
					const diagnostic = $.derived(() => errorDiagnostic($.get(displayError)));
					var div_7 = root_6();
					var div_8 = $.child(div_7);
					var text_2 = $.child(div_8, true);

					$.reset(div_8);

					var div_9 = $.sibling(div_8, 2);
					var text_3 = $.child(div_9, true);

					$.reset(div_9);

					var node_4 = $.sibling(div_9, 2);

					{
						var consequent_7 = ($$anchor) => {
							var fragment_3 = root_5();
							var div_10 = $.first_child(fragment_3);
							var text_4 = $.child(div_10);

							$.reset(div_10);

							var node_5 = $.sibling(div_10, 2);

							{
								var consequent_6 = ($$anchor) => {
									var div_11 = root_4();
									var text_5 = $.child(div_11, true);

									$.reset(div_11);
									$.template_effect(() => $.set_text(text_5, $.get(diagnostic).detail));
									$.append($$anchor, div_11);
								};

								$.if(node_5, ($$render) => {
									if ($.get(diagnostic).detail) $$render(consequent_6);
								});
							}

							var pre = $.sibling(node_5, 2);
							var text_6 = $.child(pre, true);

							$.reset(pre);

							$.template_effect(() => {
								$.set_text(text_4, `Line ${$.get(diagnostic).line ?? ''}, column ${$.get(diagnostic).column ?? ''}`);
								$.set_text(text_6, $.get(diagnostic).excerpt);
							});

							$.append($$anchor, fragment_3);
						};

						$.if(node_4, ($$render) => {
							if ($.get(diagnostic)) $$render(consequent_7);
						});
					}

					$.reset(div_7);

					$.template_effect(
						($0) => {
							$.set_text(text_2, $.get(diagnostic) ? "SlexKit syntax error" : "Failed to render SlexKit");
							$.set_text(text_3, $0);
						},
						[
							() => $.get(diagnostic)?.message ?? errorMessage($.get(displayError))
						]
					);

					$.append($$anchor, div_7);
				};

				var alternate = ($$anchor) => {
					var div_12 = root_3();

					$.action(div_12, ($$node, $$action_arg) => renderPreview?.($$node, $$action_arg), () => $.get(runtimeInput));
					$.append($$anchor, div_12);
				};

				$.if(node_3, ($$render) => {
					if ($.get(displayError)) $$render(consequent_8); else $$render(alternate, -1);
				});
			}

			$.reset(div_6);
			$.reset(div_5);

			$.template_effect(($0) => $.set_class(div_5, 1, $0), [
				() => $.clsx(`slex-streamdown-block ${$.get(blockClass)}`.trim())
			]);

			$.append($$anchor, div_5);
		};

		$.if(node_1, ($$render) => {
			if (!$.get(isSlexKit)) $$render(consequent); else if (!$.get(runtimeApi) && !$.get(runtimeLoadError)) $$render(consequent_1, 1); else if ($.get(sourcePending)) $$render(consequent_2, 2); else if ($.get(sourceKind) === "state-only") $$render(consequent_4, 3); else if ($.get(effectiveRenderMode) === "playground") $$render(consequent_5, 4); else $$render(alternate_1, -1);
		});
	}

	$.append($$anchor, fragment);
	$.pop();
}